/**
 * createFieldHook — factory for codegen-emitted `use<Entity>Field` hooks.
 *
 * Each emitted hook is a thin wrapper around the canonical TanStack
 * "per-component select" recipe:
 *
 *     const value = useQuery({
 *       ...listOpts,
 *       enabled: false,
 *       select: (data) => data.find(r => r[rowKey] === id)?.[field],
 *     }).data;
 *
 * See: https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 *      ("A very common and useful pattern is to create per-component select functions")
 *
 * The hook reads its target `queryKey` + `rowKey` from `ListContext`, which
 * is populated by `<Items>` from the parent list hook. Calling outside an
 * `<Items>` tree throws — by design; field hooks are scoped to list surfaces.
 */
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';
import { useSdkNetworkScope } from '../../provider/SdkRuntimeContext.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { ListContext } from '../context/ListContext.js';
import { activitySourceEntityKeysForField, projectActivityEntityField } from '../internal/activity-entity.js';
import { materializeNestedEntityProjection, nestedEntityIds, projectEntityField, projectionEntityQueryKey, recordOutcomeEntityDiagnostic } from '../internal/entity-projection.js';
import { isNormalizedEnvelope } from '../internal/normalized-entity.js';
import { keepPreviousDataAcrossQueryKeys } from '../internal/query-placeholder-data.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions } from '../internal/subscriber-query-fn.js';
import { recordViewerOverlayRecovery, viewerOverlayKeyForField, viewerOverlayQueryKey } from '../internal/viewer-overlay.js';
import { getFieldDisplayNormalizer } from './field-display-normalizers.js';
/**
 * Module-scope id→row index, keyed by the PagedData reference. Built
 * lazily on first field-hook select against a given data snapshot;
 * subsequent field hooks reading from the same snapshot reuse the
 * same Map. Replaces the previous O(N) `findIndex` scan that ran once
 * per field hook per data change — at N rows × M fields the cost was
 * O(N·M); now it's O(N) build + O(1) lookup, shared across all field
 * hooks observing the same list.
 *
 * The WeakMap key is the data object itself, so when TanStack
 * structural-shares a new data reference (after an applyEvent that
 * actually changed something) the old index is GC'd alongside the old
 * data — no leak window beyond a single render cycle.
 *
 * `rowKey` is captured per spec via `ListContext`; within one data
 * snapshot the rowKey is invariant (queryKey embeds the spec, spec
 * fixes the rowKey), so we don't need to key the cache on rowKey too.
 */
const FIELD_INDEX_CACHE = new WeakMap();
function getOrBuildFieldIndex(data, rowKey) {
  const cached = FIELD_INDEX_CACHE.get(data);
  if (cached !== undefined) {
    if (process.env.NODE_ENV !== 'production') {
      _bumpPerf('fieldHook.indexCacheHits');
    }
    return cached;
  }
  const index = new Map();
  const rows = data.data;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row === undefined) continue;
    const k = row[rowKey];
    if (typeof k === 'string') index.set(k, row);
  }
  FIELD_INDEX_CACHE.set(data, index);
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('fieldHook.indexCacheMisses');
    _bumpPerf('fieldHook.rowsIndexed', rows.length);
  }
  return index;
}
export function createFieldHook(specName) {
  return function useField(id, field) {
    const ctx = useContext(ListContext);
    if (ctx === null) {
      throw new Error(`[morfi-sdk] ${specName} must be called inside <Items>. ` + 'On detail pages, read the field directly from the detail hook result.');
    }
    const {
      queryKey,
      rowKey,
      entityQueryKey,
      entityProjection,
      nestedEntityProjection,
      viewerOverlay,
      viewerArgs = {},
      viewerScope = ''
    } = ctx;
    const queryClient = useQueryClient();
    const networkScope = useSdkNetworkScope();
    // MOR-859 — a normalized list hook stores the row at its own entity key;
    // the leaf subscribes to that entry (which IS the row) instead of the
    // feed's id-only `PagedData`. Classic feeds keep the id-index scan below.
    const normalized = entityQueryKey !== undefined;
    const indexField = entityProjection?.indexFields?.includes(field) === true;
    const identityViewerField = viewerOverlay?.identityFields.includes(field) === true;
    const walletViewerField = viewerOverlay?.walletFields.includes(field) === true;
    const viewerField = identityViewerField || walletViewerField;
    const overlayQueryKey = entityQueryKey !== undefined && viewerOverlay !== undefined && viewerField ? viewerOverlayKeyForField(entityQueryKey, id, field, viewerOverlay, viewerArgs, viewerScope) ?? viewerOverlayQueryKey(entityQueryKey, id, walletViewerField ? 'wallet' : 'identity', viewerScope) : undefined;
    const effectiveQueryKey = useMemo(() => overlayQueryKey ?? (normalized && !indexField ? entityQueryKey(id) : queryKey), [overlayQueryKey, normalized, indexField, entityQueryKey, id, queryKey]);
    // Per-component select — stable identity so TanStack only re-runs it when
    // `data` actually changes (docs §select.memoization). Classic `kind:
    // multiple` cache shape is `PagedData<Row>` (rows on `.data`, looked up via
    // the amortised id-index); a normalized entry IS the single row.
    //
    // An optional per-field DISPLAY normalizer rounds the selected value to
    // display resolution (e.g. `outcomes[].conviction` → 2-dp percent) so a
    // sub-display projector tick yields a `replaceEqualDeep`-equal result and
    // TanStack returns the previous reference — the leaf bails instead of
    // re-rendering ~1×/sec (MOR-887). The cache stays full precision.
    const select = useCallback(data => {
      const normalizer = getFieldDisplayNormalizer(specName, field);
      const extract = () => {
        let raw;
        if (normalized && indexField) {
          if (isNormalizedEnvelope(data)) {
            raw = data._indexFields?.[id]?.[field];
          } else if (data !== undefined) {
            const row = getOrBuildFieldIndex(data, rowKey).get(id);
            raw = row === undefined ? undefined : row[field];
          }
        } else if (normalized) {
          if (data !== undefined) {
            raw = projectEntityField(data, field, entityProjection);
          }
          // NOTE: TanStack invokes `select` only when the entry has data, so
          // a `data === undefined` branch here is unreachable. The pre-heal
          // fallback for a missing/partial entity lives in the hook body
          // below, after `resolvedValue`.
        } else {
          if (data === undefined) return undefined;
          const index = getOrBuildFieldIndex(data, rowKey);
          const row = index.get(id);
          raw = row === undefined ? undefined : row[field];
        }
        if (raw === undefined || normalizer === undefined) return raw;
        return normalizer(raw);
      };
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('fieldHook.selectCalls');
        const t0 = performance.now();
        const value = extract();
        _observe('fieldHookSelectMs', performance.now() - t0);
        return value;
      }
      return extract();
    }, [id, field, rowKey, normalized, indexField, entityProjection]);
    // Cache-resolving subscriber queryFn — see `subscriber-query-fn.ts`
    // for why this can't be a singleton. Re-built per queryKey identity.
    const queryFn = useMemo(() => createSubscriberQueryFn(queryClient, effectiveQueryKey), [queryClient, effectiveQueryKey]);
    const {
      data: value
    } = useQuery({
      queryKey: effectiveQueryKey,
      queryFn,
      ...getSubscriberQueryMetaOptions(queryClient, effectiveQueryKey),
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY,
      select,
      // Mirror the parent list query's `keepPreviousData` (see
      // `realtimeQueryOptions`). When the list's queryKey changes (filter /
      // sort switch) the field observer's `ListContext` queryKey changes
      // too; holding the previous row's field value keeps the leaf card's
      // content stable until the new list page settles, instead of
      // flashing `undefined` through the fallback.
      placeholderData: (previousData, previousQuery) => keepPreviousDataAcrossQueryKeys(effectiveQueryKey, previousData, previousQuery)
    });
    const activityEntity = normalized && entityProjection?.resource === 'activity' ? queryClient.getQueryData(entityQueryKey(id)) : undefined;
    const activitySourceKeys = activityEntity === undefined ? [] : activitySourceEntityKeysForField(queryClient, activityEntity, networkScope, field);
    const activitySources = useQueries({
      queries: activitySourceKeys.map(sourceKey => ({
        queryKey: sourceKey,
        queryFn: createSubscriberQueryFn(queryClient, sourceKey),
        ...getSubscriberQueryMetaOptions(queryClient, sourceKey),
        enabled: false,
        staleTime: Number.POSITIVE_INFINITY
      }))
    });
    const activityProjection = activityEntity === undefined ? {
      found: false
    } : projectActivityEntityField(activityEntity, field, activitySourceKeys, activitySources.map(source => source.data));
    const resolvedValue = activityProjection.found ? activityProjection.value : value;
    // Pre-heal fallback. The entity entry can be missing (the SSR render, or a
    // client render before the mount heal splits the feed) or partial (healed
    // from a projected sibling query that lacks this field). TanStack invokes
    // `select` only when the entry has data, so this fallback cannot live
    // inside `select` — an in-select `data === undefined` branch never runs,
    // which left the leaf rendering `undefined` during SSR while a raced heal
    // could populate the value mid-hydration on the client (structurally
    // different trees server vs client → React #418 on every anonymous
    // /demo-mode boot, 2026-09-02). Resolving from the hydrated feed row here
    // keeps the server render and every client render on one path; once the
    // heal completes the subscribed entity value is defined and this branch
    // reads nothing. `undefined`-only check on purpose — `null` is a real
    // cleared value and must not fall through to the stale feed row.
    let fallbackValue;
    if (resolvedValue === undefined && normalized && !indexField && overlayQueryKey === undefined) {
      const listData = queryClient.getQueryData(queryKey);
      if (listData !== undefined && !isNormalizedEnvelope(listData)) {
        const row = getOrBuildFieldIndex(listData, rowKey).get(id);
        if (row !== undefined && typeof row !== 'string') {
          const raw = row[field];
          const normalizer = getFieldDisplayNormalizer(specName, field);
          fallbackValue = raw !== undefined && normalizer !== undefined ? normalizer(raw) : raw;
        }
      }
    }
    const effectiveValue = resolvedValue !== undefined ? resolvedValue : fallbackValue;
    const nestedIds = nestedEntityProjection?.field === field ? nestedEntityIds({
      [field]: effectiveValue
    }, nestedEntityProjection) : [];
    useQueries({
      queries: nestedEntityProjection === undefined ? [] : nestedIds.map(nestedId => {
        const nestedKey = projectionEntityQueryKey(nestedEntityProjection, nestedId);
        return {
          queryKey: nestedKey,
          queryFn: createSubscriberQueryFn(queryClient, nestedKey),
          enabled: false,
          staleTime: Number.POSITIVE_INFINITY
        };
      })
    });
    // `useQueries` subscribes the leaf to each outcome entity, but its
    // observer results can briefly lag behind a synchronous cache write. Read
    // the cache itself to decide whether the nested DTO is complete. Returning
    // the reference-only row would violate the generated field type (for
    // example, `label` is absent) and can crash a consumer while recovery is
    // underway.
    const missingNestedIds = nestedEntityProjection === undefined ? [] : nestedIds.filter(nestedId => queryClient.getQueryData(projectionEntityQueryKey(nestedEntityProjection, nestedId)) === undefined);
    const nestedRecoveryKey = missingNestedIds.join('|');
    const nestedRecoveryRef = useRef(null);
    useEffect(() => {
      if (nestedEntityProjection === undefined || nestedRecoveryKey.length === 0) {
        nestedRecoveryRef.current = null;
        return;
      }
      const recoveringIds = nestedRecoveryKey.split('|');
      if (recoveringIds.every(nestedId => queryClient.getQueryData(projectionEntityQueryKey(nestedEntityProjection, nestedId)) !== undefined)) {
        nestedRecoveryRef.current = null;
        return;
      }
      if (nestedRecoveryRef.current === nestedRecoveryKey) return;
      nestedRecoveryRef.current = nestedRecoveryKey;
      void refetchRealtimeQuery(queryClient, queryKey).then(outcome => {
        const stillMissing = recoveringIds.some(nestedId => queryClient.getQueryData(projectionEntityQueryKey(nestedEntityProjection, nestedId)) === undefined);
        if (nestedEntityProjection.resource === 'outcomes' && (outcome !== 'success' || stillMissing)) {
          recordOutcomeEntityDiagnostic({
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(nestedRecoveryKey),
            source: 'list-field'
          });
        }
      });
    }, [nestedRecoveryKey, queryClient, queryKey, nestedEntityProjection]);
    // Defensive recovery: an entity can be explicitly removed by a cache
    // reset/devtool even though normalized entities are retained from normal
    // GC. If the feed still references this id, refetch through the mounted
    // data-owning list observer; the canonical split repopulates the entity.
    useEffect(() => {
      if (!normalized) return;
      if (queryClient.getQueryData(effectiveQueryKey) !== undefined) return;
      const feed = queryClient.getQueryData(queryKey);
      if (!isNormalizedEnvelope(feed) || !feed.data.includes(id)) return;
      if (viewerField) recordViewerOverlayRecovery(id, field);
      if (effectiveQueryKey[0] === 'positions' && process.env.NODE_ENV !== 'production') {
        _bumpPerf('positionEntity.missingRecoveries');
      }
      void refetchRealtimeQuery(queryClient, queryKey);
    }, [normalized, viewerField, field, queryClient, effectiveQueryKey, queryKey, id]);
    if (nestedEntityProjection?.field !== field) return effectiveValue;
    if (missingNestedIds.length > 0) return undefined;
    const materialized = materializeNestedEntityProjection(queryClient, {
      [field]: effectiveValue
    }, nestedEntityProjection);
    return materialized[field];
  };
}